import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, Mail, Play, Pause, Lock, Settings, Eye, Copy, MapPin, AlertTriangle } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
export function MailingLists() {
  const {
    newsletters,
    forms,
    duplicateNewsletter,
    updateNewsletterIntake
  } = useWorkspace();
  const [showIntakeModal, setShowIntakeModal] = useState<number | null>(null);
  const [intakeMessage, setIntakeMessage] = useState('');
  const getFormName = (formId?: number) => {
    if (!formId) return null;
    return forms.find(f => f.id === formId)?.name || 'Unknown Form';
  };
  const getIntakeStatusConfig = (status: string) => {
    switch (status) {
      case 'open':
        return {
          icon: Play,
          color: 'text-green-600',
          bg: 'bg-green-50',
          border: 'border-green-200',
          label: 'Accepting Signups'
        };
      case 'paused':
        return {
          icon: Pause,
          color: 'text-yellow-600',
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          label: 'Paused'
        };
      case 'closed':
        return {
          icon: Lock,
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-200',
          label: 'Closed'
        };
      default:
        return {
          icon: Play,
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          label: 'Unknown'
        };
    }
  };
  const handleIntakeChange = (id: number, newStatus: 'open' | 'paused' | 'closed') => {
    if (newStatus === 'open') {
      updateNewsletterIntake(id, newStatus);
      setShowIntakeModal(null);
    } else {
      setShowIntakeModal(id);
    }
  };
  const confirmIntakeChange = (id: number, status: 'paused' | 'closed') => {
    updateNewsletterIntake(id, status, intakeMessage);
    setShowIntakeModal(null);
    setIntakeMessage('');
  };
  return <div className="space-y-6" data-id="element-1336">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1337">
        <div className="min-w-0" data-id="element-1338">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1339">
            Mailing Lists
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1340">
            Manage your audience lists, intake controls, and subscribers.
          </p>
        </div>
        <Link to="/newsletters/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-1341">
          <Plus className="h-4 w-4 flex-shrink-0" data-id="element-1342" />
          <span data-id="element-1343">Create List</span>
        </Link>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1344">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1345">
          <div className="flex items-center gap-3" data-id="element-1346">
            <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-1347">
              <Mail className="h-5 w-5 text-blue-600" data-id="element-1348" />
            </div>
            <div className="min-w-0" data-id="element-1349">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1350">
                {newsletters.length}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-1351">Total Lists</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1352">
          <div className="flex items-center gap-3" data-id="element-1353">
            <div className="p-2 bg-green-50 rounded-lg flex-shrink-0" data-id="element-1354">
              <Play className="h-5 w-5 text-green-600" data-id="element-1355" />
            </div>
            <div className="min-w-0" data-id="element-1356">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1357">
                {newsletters.filter(n => n.intakeStatus === 'open').length}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-1358">
                Accepting Signups
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1359">
          <div className="flex items-center gap-3" data-id="element-1360">
            <div className="p-2 bg-yellow-50 rounded-lg flex-shrink-0" data-id="element-1361">
              <Pause className="h-5 w-5 text-yellow-600" data-id="element-1362" />
            </div>
            <div className="min-w-0" data-id="element-1363">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1364">
                {newsletters.filter(n => n.intakeStatus === 'paused').length}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-1365">Paused</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1366">
          <div className="flex items-center gap-3" data-id="element-1367">
            <div className="p-2 bg-purple-50 rounded-lg flex-shrink-0" data-id="element-1368">
              <Users className="h-5 w-5 text-purple-600" data-id="element-1369" />
            </div>
            <div className="min-w-0" data-id="element-1370">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-1371">
                {newsletters.reduce((sum, n) => sum + n.subscriberCount, 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 truncate" data-id="element-1372">
                Total Subscribers
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mailing Lists Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-1373">
        {newsletters.map(list => {
        const intakeConfig = getIntakeStatusConfig(list.intakeStatus);
        const IntakeIcon = intakeConfig.icon;
        const formName = getFormName(list.formId);
        const isNearLimit = list.intakeLimit && list.subscriberCount >= list.intakeLimit * 0.9;
        return <div key={list.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col" data-id="element-1374">
              {/* Header with intake status */}
              <div className={`px-4 py-3 ${intakeConfig.bg} border-b ${intakeConfig.border} flex-shrink-0`} data-id="element-1375">
                <div className="flex items-center justify-between gap-2" data-id="element-1376">
                  <div className="flex items-center gap-2 min-w-0" data-id="element-1377">
                    <IntakeIcon className={`h-4 w-4 ${intakeConfig.color} flex-shrink-0`} data-id="element-1378" />
                    <span className={`text-sm font-medium ${intakeConfig.color} truncate`} data-id="element-1379">
                      {intakeConfig.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-1380">
                    {list.intakeStatus !== 'open' && <button onClick={() => handleIntakeChange(list.id, 'open')} className="p-1 hover:bg-white/50 rounded text-green-600" title="Open Intake" data-id="element-1381">
                        <Play className="h-4 w-4" data-id="element-1382" />
                      </button>}
                    {list.intakeStatus !== 'paused' && <button onClick={() => handleIntakeChange(list.id, 'paused')} className="p-1 hover:bg-white/50 rounded text-yellow-600" title="Pause Intake" data-id="element-1383">
                        <Pause className="h-4 w-4" data-id="element-1384" />
                      </button>}
                    {list.intakeStatus !== 'closed' && <button onClick={() => handleIntakeChange(list.id, 'closed')} className="p-1 hover:bg-white/50 rounded text-red-600" title="Close Intake" data-id="element-1385">
                        <Lock className="h-4 w-4" data-id="element-1386" />
                      </button>}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4 flex-1" data-id="element-1387">
                <div className="min-w-0" data-id="element-1388">
                  <h3 className="text-lg font-medium text-gray-900 truncate" data-id="element-1389">
                    {list.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2" data-id="element-1390">
                    {list.description}
                  </p>
                </div>

                {/* Subscriber count with progress */}
                <div className="space-y-2" data-id="element-1391">
                  <div className="flex items-center justify-between text-sm" data-id="element-1392">
                    <span className="text-gray-600" data-id="element-1393">Subscribers</span>
                    <span className="font-semibold text-gray-900" data-id="element-1394">
                      {list.subscriberCount.toLocaleString()}
                    </span>
                  </div>
                  {list.intakeLimit && <>
                      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-1395">
                        <div className={`h-2 rounded-full ${isNearLimit ? 'bg-yellow-500' : 'bg-blue-500'}`} style={{
                    width: `${Math.min(list.subscriberCount / list.intakeLimit * 100, 100)}%`
                  }} data-id="element-1396" />
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-1397">
                        <span data-id="element-1398">Limit: {list.intakeLimit.toLocaleString()}</span>
                        {isNearLimit && <span className="flex items-center gap-1 text-yellow-600" data-id="element-1399">
                            <AlertTriangle className="h-3 w-3 flex-shrink-0" data-id="element-1400" />
                            <span className="whitespace-nowrap" data-id="element-1401">
                              Near capacity
                            </span>
                          </span>}
                      </div>
                    </>}
                </div>

                {/* Meta info */}
                <div className="flex flex-wrap gap-2 text-xs" data-id="element-1402">
                  {list.subRegion && <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded whitespace-nowrap" data-id="element-1403">
                      <MapPin className="h-3 w-3 flex-shrink-0" data-id="element-1404" />
                      {list.subRegion}
                    </span>}
                  {formName && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded whitespace-nowrap max-w-full" data-id="element-1405">
                      <Mail className="h-3 w-3 flex-shrink-0" data-id="element-1406" />
                      <span className="truncate" data-id="element-1407">{formName}</span>
                    </span>}
                </div>

                {/* Paused/Closed message preview */}
                {list.intakeStatus === 'paused' && list.intakePausedMessage && <div className="bg-yellow-50 border border-yellow-100 rounded p-2 text-xs text-yellow-700" data-id="element-1408">
                    <strong data-id="element-1409">Paused message:</strong> {list.intakePausedMessage}
                  </div>}
                {list.intakeStatus === 'closed' && list.intakeClosedMessage && <div className="bg-red-50 border border-red-100 rounded p-2 text-xs text-red-700" data-id="element-1410">
                    <strong data-id="element-1411">Closed message:</strong> {list.intakeClosedMessage}
                  </div>}
              </div>

              {/* Actions */}
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between flex-shrink-0" data-id="element-1412">
                <Link to={`/mailing-lists/${list.id}`} className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 whitespace-nowrap" data-id="element-1413">
                  <Eye className="h-4 w-4 flex-shrink-0" data-id="element-1414" />
                  <span data-id="element-1415">View Subscribers</span>
                </Link>
                <div className="flex items-center gap-2" data-id="element-1416">
                  <button onClick={() => duplicateNewsletter(list.id)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" title="Duplicate" data-id="element-1417">
                    <Copy className="h-4 w-4" data-id="element-1418" />
                  </button>
                  <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" title="Settings" data-id="element-1419">
                    <Settings className="h-4 w-4" data-id="element-1420" />
                  </button>
                </div>
              </div>
            </div>;
      })}
      </div>

      {/* Intake Control Modal */}
      {showIntakeModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1421">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-1422">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowIntakeModal(null)} data-id="element-1423" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-1424">
              <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1425">
                Configure Intake Message
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1426">
                This message will be shown to users when they try to sign up
                while the list is paused or closed.
              </p>
              <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="e.g., We're currently at capacity. Please check back soon!" value={intakeMessage} onChange={e => setIntakeMessage(e.target.value)} data-id="element-1427" />
              <div className="flex justify-end gap-3 mt-4" data-id="element-1428">
                <button onClick={() => setShowIntakeModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap" data-id="element-1429">
                  Cancel
                </button>
                <button onClick={() => {
              const newsletter = newsletters.find(n => n.id === showIntakeModal);
              if (newsletter) {
                const newStatus = newsletter.intakeStatus === 'open' ? 'paused' : 'closed';
                confirmIntakeChange(showIntakeModal, newStatus as 'paused' | 'closed');
              }
            }} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-1430">
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}